import AppBadge from '@/components/ui/AppBade';
import { AppCard } from '@/components/ui/AppCard';
import { Skeleton } from '@/components/ui/Skeleton';
import { endpoints } from '@/services/endpoints';
import { axiosGetRequest } from '@/services/Request';
import { getSocket } from '@/socket';
import { Logs } from '@/types/dashboard.types';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { createRef, useEffect, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { v4 as uuidv4 } from 'uuid';

function LogsComponent({ className = '' }) {
  const socket = getSocket();

  const [logs, setLogs] = useState<Logs[]>([]);
  type logPayload = {
    bookId: string;
    status: string;
  };
  const { data: allLogs, isLoading: isLoadingLogs } = useQuery({
    queryKey: [endpoints.getAllBooks],
    queryFn: () => axiosGetRequest(endpoints.logs),
    select: data => data.result,
  });

  useEffect(() => {
    const refactoredLogs = allLogs?.map(log => ({
      ...log,
      ref: createRef<HTMLDivElement>() as React.RefObject<HTMLDivElement>,
      uuid: uuidv4(),
    }));
    setLogs(refactoredLogs || []);
  }, [allLogs]);

  useEffect(() => {
    if (!socket) return;

    const handleBookStatusChange = (payload: logPayload) => {
      const newLog: Logs = {
        _id: payload.bookId,
        time: new Date().toISOString(),
        message: payload.status,
        bookId: payload.bookId,
        status: payload.status,
        user: 'system',
        action_type: 'status_change',
        uuid: uuidv4(),
        ref: createRef<HTMLDivElement>() as React.RefObject<HTMLDivElement>,
      };
      setLogs(prev => [newLog, ...prev]);
    };

    socket.on('bookStatusChange', handleBookStatusChange);

    return () => {
      socket.off('bookStatusChange', handleBookStatusChange);
    };
  }, [socket]);

  return (
    <div className={className}>
      <AppCard className="max-h-full overflow-auto">
        <h3 className="mb-4 text-base font-semibold text-coal-900">Activity Logs</h3>
        {isLoadingLogs ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 12 }).map((_, index) => (
              <LogsSkeleton key={index} />
            ))}
          </div>
        ) : logs?.length === 0 ? (
          <p className="py-10 text-center text-sm text-coal-400">No logs yet</p>
        ) : (
          <TransitionGroup className="logs-list space-y-1.5 text-sm">
            {logs?.map(log => (
              <CSSTransition key={log.uuid} nodeRef={log.ref} timeout={500} classNames="log-item">
                <div
                  ref={log.ref}
                  className="log-item flex items-center justify-between rounded-lg bg-coal-100 px-3 py-2"
                >
                  <div className="flex items-center gap-2">
                    <p className="text-sm text-coal-700">{log.message}</p>
                    {log.status === 'pending' && (
                      <AppBadge size="small" variant="label" color="warning">
                        Pending
                      </AppBadge>
                    )}
                    {log.status === 'approved' && (
                      <AppBadge size="small" variant="label" color="success">
                        Approved
                      </AppBadge>
                    )}
                    {log.status === 'rejected' && (
                      <AppBadge size="small" variant="label" color="error">
                        Rejected
                      </AppBadge>
                    )}
                  </div>
                  <span className="text-xs text-coal-400">{moment(log.time).format('DD-MM-YYYY - HH:mm a')}</span>
                </div>
              </CSSTransition>
            ))}
          </TransitionGroup>
        )}
      </AppCard>
    </div>
  );
}

export default LogsComponent;

const LogsSkeleton = () => {
  return (
    <div className="flex justify-between gap-3 rounded-lg bg-coal-100/50 p-2.5">
      <Skeleton className="h-4 w-full rounded" />
      <Skeleton className="h-4 w-full rounded" />
      <Skeleton className="h-4 w-20 rounded" />
    </div>
  );
};
